import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import {
  fetchLocalCompetitorProfile,
  fetchLocalDiscovery,
  fetchLocalLocations,
  fetchLocalPlaceId,
  fetchSeoProjectOptions,
  type LocalCompetitorProfileResponse,
  type LocalDiscoveryResponse,
  type PlaceIdResponse,
} from '@/data/sources';
import type { LocalLocationOption } from '@/types';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/local-growth — Local Growth (Beta). Read-only local-intelligence
 * surface over licensed DataForSEO Business Data + free Google Places ids.
 *
 * HONESTY + SAFETY INVARIANTS (LGB contract):
 *  - NEVER auto-fires a paid provider call: every read runs ONLY on an explicit
 *    user action, so mounting this page triggers nothing paid (project options
 *    is a free list read). No useQuery is wired to the paid endpoints.
 *  - EVERY data card is source-attributed (DataForSEO Business Data / Google
 *    Places) with the provider's fetched_at and a cache indicator.
 *  - Provider nulls render as explicit em-dashes — numbers are NEVER invented.
 *  - Honest states throughout: idle / loading / error / not-configured. Absent
 *    creds surface as a "not configured" panel (503), never fabricated data.
 *  - Google Business Profile management is a LOCKED capability: its actions are
 *    rendered visibly disabled with the exact pending string — no dead links,
 *    no fake toggles. GBP OAuth/posting/review-replies/editing are out of scope.
 */

const GBP_PENDING = 'Google Business Profile management access pending.';
const DEFAULT_LOCATION_CODE = 2826; // United Kingdom (DataForSEO location_code)

interface Panel {
  title: string;
  body: string;
}

/** Map a machine source tag to its human, source-attributed label. */
function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo')) return 'DataForSEO Business Data';
  if (source.startsWith('google.places')) return 'Google Places';
  return source;
}

/** One provider read's lifecycle. Never react-query — so nothing auto-fires. */
type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

/** Turn an unknown fetch failure into honest human guidance. `free` tailors the
 *  429 wording for the free place-id daily cap vs a paid rate limit. */
function describeError(err: unknown, opts: { free?: boolean } = {}): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Local Growth isn’t connected yet',
        body: 'Live local intelligence needs the provider connections (DataForSEO Business Data, Google Places), which aren’t configured for this environment yet. Nothing was fetched and nothing was charged — this is an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return opts.free
        ? {
            title: 'Daily place lookup limit reached',
            body: 'This workspace has used its allowance of live place-id lookups for today (a hard, free-tier daily cap). Nothing was charged — resolutions resume as the 24-hour window rolls over.',
          }
        : {
            title: 'Provider rate limit reached',
            body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
          };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. Lookups resume when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 400) {
      return {
        title: 'That search couldn’t be run',
        body: err.message || 'The search terms were missing or invalid — adjust them and try again.',
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

/** em-dash for any provider field the response left null — never a fake value. */
function orDash(v: string | number | null): string {
  return v === null || v === '' ? '—' : String(v);
}

/** Source + freshness + cache footer, shown on EVERY data card. */
function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle pt-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 text-sm text-text-primary">{value}</div>
    </div>
  );
}

export function SeoLocalGrowthPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Project options is a FREE list read — safe to fetch on mount. The paid
  // Local Growth endpoints are never wired to a query; they run on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  // Per-section inputs + lifecycle (independent, user-triggered).
  const [discoveryQuery, setDiscoveryQuery] = useState('');
  const [discoveryLocation, setDiscoveryLocation] = useState('uk');
  // Free config read (no provider call) — safe to load on mount for the picker.
  const locationsQuery = useQuery({ queryKey: ['local-locations'], queryFn: fetchLocalLocations });
  const locationOptions: LocalLocationOption[] = locationsQuery.data ?? [
    { key: 'uk', label: 'United Kingdom (no geo filter)' },
  ];
  const [discovery, setDiscovery] = useState<SectionState<LocalDiscoveryResponse>>({ status: 'idle' });

  const [competitorKeyword, setCompetitorKeyword] = useState('');
  const [competitorLocation, setCompetitorLocation] = useState(String(DEFAULT_LOCATION_CODE));
  const [competitor, setCompetitor] = useState<SectionState<LocalCompetitorProfileResponse>>({
    status: 'idle',
  });

  const [placeQuery, setPlaceQuery] = useState('');
  const [place, setPlace] = useState<SectionState<PlaceIdResponse>>({ status: 'idle' });

  /** Parse a location_code input; blank/invalid → undefined (API defaults). */
  const parseLocation = (raw: string): number | undefined => {
    const n = Number(raw.trim());
    return raw.trim() !== '' && Number.isInteger(n) && n > 0 ? n : undefined;
  };

  const runDiscovery = async () => {
    if (projectId === null || discovery.status === 'loading' || discoveryQuery.trim() === '') return;
    setDiscovery({ status: 'loading' });
    try {
      const data = await fetchLocalDiscovery(projectId, {
        query: discoveryQuery.trim(),
        location: discoveryLocation,
      });
      setDiscovery({ status: 'done', data });
    } catch (err) {
      setDiscovery({ status: 'error', panel: describeError(err) });
    }
  };

  const runCompetitor = async () => {
    if (projectId === null || competitor.status === 'loading' || competitorKeyword.trim() === '') return;
    setCompetitor({ status: 'loading' });
    try {
      const data = await fetchLocalCompetitorProfile(projectId, {
        keyword: competitorKeyword.trim(),
        location_code: parseLocation(competitorLocation),
      });
      setCompetitor({ status: 'done', data });
    } catch (err) {
      setCompetitor({ status: 'error', panel: describeError(err) });
    }
  };

  const runPlace = async () => {
    if (projectId === null || place.status === 'loading' || placeQuery.trim() === '') return;
    setPlace({ status: 'loading' });
    try {
      const data = await fetchLocalPlaceId(projectId, { query: placeQuery.trim() });
      setPlace({ status: 'done', data });
    } catch (err) {
      setPlace({ status: 'error', panel: describeError(err, { free: true }) });
    }
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Local Growth" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Local Growth" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Local Growth" />
        <EmptyState
          title="No project to explore yet"
          teach="Local Growth runs against an SEO project. Add your first domain on the Projects page, then come back here to run discovery, competitor and place-id lookups."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => void select(Number(e.target.value))}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Local Growth"
        subtitle="Local business intelligence over licensed DataForSEO Business Data and free Google Places ids — every result source-attributed, on-demand, and never a proprietary-index claim."
        actions={
          <>
            <Badge tone="ai">Beta</Badge>
            {projectSelector}
          </>
        }
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Discovery and competitor lookups use paid provider data, so they’re limited to workspace
          owners and operators. You can still resolve free Google Places ids below.
        </div>
      )}

      {/* (a) Discovery search panel — user-triggered, never auto-fired ------- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">Discovery</Badge>
          <span className="text-sm font-medium text-text-primary">Find local businesses</span>
          <span className="ml-auto text-xs text-text-tertiary">DataForSEO Business Data · paid, budget-guarded</span>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Search licensed business listings for a service + place. Runs only when you press Search —
          nothing is fetched or charged on load.
        </p>
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runDiscovery();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-text-secondary">Search</span>
            <input
              value={discoveryQuery}
              onChange={(e) => setDiscoveryQuery(e.target.value)}
              placeholder="e.g. pilates studio cardiff"
              className={`${inputClass} w-64`}
              aria-label="Discovery search terms"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-text-secondary">Location</span>
            <select
              value={discoveryLocation}
              onChange={(e) => setDiscoveryLocation(e.target.value)}
              className={`${inputClass} w-56`}
              aria-label="Discovery location"
              title="Curated city list (geo-filtered search); UK-wide applies no geo filter"
            >
              {locationOptions.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || discovery.status === 'loading' || discoveryQuery.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {discovery.status === 'loading' ? 'Searching…' : 'Search'}
          </Button>
        </form>

        <div className="mt-4">
          {discovery.status === 'idle' && (
            <p className="text-sm text-text-tertiary">No data yet — run a search to see local listings.</p>
          )}
          {discovery.status === 'loading' && <LoadingState rows={3} />}
          {discovery.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{discovery.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{discovery.panel.body}</p>
            </div>
          )}
          {discovery.status === 'done' &&
            (discovery.data.results.length === 0 ? (
              <p className="text-sm text-text-secondary">
                No listings matched that search — that’s the provider’s real answer, not an error.
              </p>
            ) : (
              <div className="space-y-3">
                {discovery.data.results.map((r, i) => (
                  <div key={r.cid ?? `${r.name}-${i}`} className="rounded-md border border-border-subtle bg-canvas p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-text-primary">{r.name}</span>
                      {r.category && <Badge tone="neutral">{r.category}</Badge>}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                      <Field label="Rating" value={orDash(r.rating)} />
                      <Field label="Reviews" value={orDash(r.review_count)} />
                      <Field label="Phone" value={orDash(r.phone)} />
                      <Field label="CID" value={orDash(r.cid)} />
                    </div>
                    <div className="mt-2 text-sm text-text-secondary">{orDash(r.address)}</div>
                  </div>
                ))}
                <SourceFooter
                  source={discovery.data.source}
                  fetchedAt={discovery.data.fetched_at}
                  cacheHit={discovery.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>

      {/* (b) Competitor profile card — user-triggered ----------------------- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">Competitor</Badge>
          <span className="text-sm font-medium text-text-primary">Competitor profile</span>
          <span className="ml-auto text-xs text-text-tertiary">DataForSEO Business Data · paid, budget-guarded</span>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Pull one competitor’s public Google Business Profile snapshot. Runs only on Look up.
        </p>
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runCompetitor();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-text-secondary">Business / keyword</span>
            <input
              value={competitorKeyword}
              onChange={(e) => setCompetitorKeyword(e.target.value)}
              placeholder="e.g. Cardiff Pilates Studio"
              className={`${inputClass} w-64`}
              aria-label="Competitor business name or keyword"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-text-secondary">Location code</span>
            <input
              value={competitorLocation}
              onChange={(e) => setCompetitorLocation(e.target.value)}
              inputMode="numeric"
              className={`${inputClass} w-28`}
              aria-label="DataForSEO location code"
              title="DataForSEO location code — 2826 = United Kingdom"
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || competitor.status === 'loading' || competitorKeyword.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {competitor.status === 'loading' ? 'Looking up…' : 'Look up'}
          </Button>
        </form>

        <div className="mt-4">
          {competitor.status === 'idle' && (
            <p className="text-sm text-text-tertiary">No data yet — look up a competitor to see their profile.</p>
          )}
          {competitor.status === 'loading' && <LoadingState rows={3} />}
          {competitor.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{competitor.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{competitor.panel.body}</p>
            </div>
          )}
          {competitor.status === 'done' &&
            (competitor.data.profile === null ? (
              <p className="text-sm text-text-secondary">
                No Google Business Profile matched that lookup — that’s the provider’s real answer, not an error.
              </p>
            ) : (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">{competitor.data.profile.name}</span>
                  {competitor.data.profile.category && (
                    <Badge tone="neutral">{competitor.data.profile.category}</Badge>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                  <Field label="Rating" value={orDash(competitor.data.profile.rating)} />
                  <Field label="Reviews" value={orDash(competitor.data.profile.review_count)} />
                  <Field label="Phone" value={orDash(competitor.data.profile.phone)} />
                  <Field label="Website" value={orDash(competitor.data.profile.website)} />
                  <Field label="Hours" value={orDash(competitor.data.profile.hours)} />
                  <Field label="CID" value={orDash(competitor.data.profile.cid)} />
                </div>
                <div className="mt-3 text-sm text-text-secondary">{orDash(competitor.data.profile.address)}</div>
                <SourceFooter
                  source={competitor.data.source}
                  fetchedAt={competitor.data.fetched_at}
                  cacheHit={competitor.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>

      {/* (c) Place-id resolution chip — free SKU, user-triggered ------------- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">Place ID</Badge>
          <span className="text-sm font-medium text-text-primary">Resolve a Google place ID</span>
          <span className="ml-auto text-xs text-text-tertiary">Google Places · free · ≤3 live lookups/day</span>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Resolve the free Google Places id for a business. No provider charge; a hard daily cap
          protects the free allowance.
        </p>
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runPlace();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-text-secondary">Business + place</span>
            <input
              value={placeQuery}
              onChange={(e) => setPlaceQuery(e.target.value)}
              placeholder="e.g. Cardiff Pilates Studio, Cardiff"
              className={`${inputClass} w-72`}
              aria-label="Place-id search terms"
            />
          </label>
          <Button
            type="submit"
            variant="secondary"
            disabled={place.status === 'loading' || placeQuery.trim() === ''}
          >
            {place.status === 'loading' ? 'Resolving…' : 'Resolve place ID'}
          </Button>
        </form>

        <div className="mt-4">
          {place.status === 'idle' && (
            <p className="text-sm text-text-tertiary">No data yet — resolve a place ID to see the result.</p>
          )}
          {place.status === 'loading' && <LoadingState rows={1} />}
          {place.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{place.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{place.panel.body}</p>
            </div>
          )}
          {place.status === 'done' && (
            <div className="rounded-md border border-border-subtle bg-canvas p-3">
              {place.data.place_id === null ? (
                <p className="text-sm text-text-secondary">
                  Google Places returned no matching place for that search — an honest empty result, not an error.
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-text-tertiary">place_id</span>
                  <code className="rounded-sm bg-inset px-2 py-1 font-mono text-sm text-text-primary" data-tabular>
                    {place.data.place_id}
                  </code>
                </div>
              )}
              <SourceFooter
                source={place.data.source}
                fetchedAt={place.data.fetched_at}
                cacheHit={place.data.cache_hit}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Locked capability — Google Business Profile management (pending) ---- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="warning">Locked</Badge>
          <span className="text-sm font-medium text-text-primary">Google Business Profile management</span>
        </div>
        <p className="mt-2 text-sm text-text-secondary">{GBP_PENDING}</p>
        <div className="mt-3 flex flex-wrap gap-2" aria-disabled="true">
          {['Post an update', 'Reply to a review', 'Edit profile details'].map((label) => (
            <Button key={label} variant="secondary" disabled aria-disabled="true" title={GBP_PENDING}>
              {label}
            </Button>
          ))}
        </div>
        <p className="mt-3 text-[11px] leading-snug text-text-tertiary">
          Local Growth is read-only in Beta. Posting, review replies and profile editing require
          Google Business Profile write access, which isn’t enabled — these actions are shown
          disabled rather than hidden so the roadmap stays honest.
        </p>
      </Card>
    </div>
  );
}
